import { HttpInterceptorFn } from '@angular/common/http';

/**
 * 🔴 SONDE TEMPORAIRE — `grep -rn "CALC-TRACE" src/` pour tout retirer.
 *
 * Elle repond a UNE question, restee sans reponse toute la journee du 2026-09-16 :
 * **qui emet chaque `POST /portfolios/allocation/calculate`, et que contient-il ?**
 *
 * Plusieurs chaines recalculent l'allocation en parallele et s'ecrasent l'une l'autre. Les corriger en
 * deduisant l'emetteur depuis l'ordre des cles du payload a echoue six fois de suite : deux generateurs
 * distincts produisent la meme forme.
 *
 * Posee au niveau HTTP plutot que sur les ~30 sites d'appel : un seul point de passage, aucun risque sur
 * le code metier, et rien ne peut lui echapper — y compris la couche Orval, qu'on n'a pas le droit
 * d'editer.
 *
 * ⚠️ ACTIVE PAR UN DRAPEAU EXPLICITE, PAS PAR `isDevMode()`.
 *
 * Elle l'etait, et elle s'est tue : les configurations `uat` et `production` ont `optimization: true`,
 * donc `isDevMode()` y vaut `false`. Le reporter testait sur UAT — « y a rien dans la console ».
 *
 * Elle s'arme donc depuis la console, et reste eteinte par defaut partout :
 *
 *     localStorage.setItem('CALC_TRACE', '1')   // puis recharger
 *     localStorage.removeItem('CALC_TRACE')     // pour l'eteindre
 *
 * `@sentry/angular` capture `console.*` en breadcrumbs : ces montants de portefeuille partiraient chez
 * Sentry a la premiere erreur venue. Le drapeau est donc un geste DELIBERE, jamais un defaut — et la
 * sonde reste temporaire : `grep -rn "CALC-TRACE" src/`.
 */
const isTraceEnabled = (): boolean => {
    try {
        return localStorage.getItem('CALC_TRACE') === '1';
    } catch {
        // Fenetre privee, stockage bloque : la sonde reste eteinte, jamais d'exception.
        return false;
    }
};

export const calculateTraceInterceptor: HttpInterceptorFn = (req, next) => {
    if (isTraceEnabled() && req.url.includes('/portfolios/allocation/calculate')) {
        const body = req.body as {
            portfolioId?: number,
            totalAmount?: number,
            shares?: unknown[],
            cashes?: unknown[],
            nextAllocation?: unknown,
        } | null;
        const isDifference = req.url.endsWith('/difference');
        // La pile nomme l'emetteur reel. On saute les cadres du framework pour garder les lignes utiles.
        const stack = (new Error().stack ?? '')
            .split('\n')
            .filter(line => /proposition|allocation|portfolio|toolbar|deposit/i.test(line)
                && !line.includes('calculate-trace'))
            .slice(0, 6)
            .map(line => line.trim());

        // 🔴 AUCUN MONTANT, AUCUN CONTENU DE LIGNE — revue de securite du 2026-09-16.
        //
        // `@sentry/angular` capture `console.*` en breadcrumbs : un `totalAmount` ou une liste de
        // positions partirait chez Sentry a la premiere erreur venue, et la sonde peut etre armee sur
        // UAT. On ne logge donc que des COMPTES et l'emetteur.
        //
        // Le diagnostic n'y perd rien : le contenu des payloads se lit dans l'onglet Network. Ce que
        // cette sonde apporte, et qu'on ne peut lire nulle part ailleurs, c'est QUI emet — deux
        // generateurs distincts produisent la meme forme de payload, et c'est precisement ce qui a fait
        // corriger le mauvais six fois de suite.
        console.warn(`[CALC-TRACE] ${isDifference ? 'DIFFERENCE' : 'CALCULATE'}`, {
            aUnPortfolioId: typeof body?.portfolioId === 'number',
            aUnTotalAmount: typeof body?.totalAmount === 'number',
            nbShares: body?.shares?.length ?? null,
            nbCashes: body?.cashes?.length ?? null,
            emetteur: stack,
        });
    }
    return next(req);
};
